import { AlertTriangle, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  useDisconnectedAccounts,
  VARIANT_SCOPE,
  type DisconnectedAccountVariant,
} from "@/lib/hooks/use-disconnected-accounts";

/**
 * Notification banner shown when a previously-connected key account has been
 * disconnected. There are two flavors:
 *  - Personal account (Google / Microsoft / Slack): shown to the affected user.
 *    Email + calendar sync, meeting recording, and contact enrichment all
 *    silently fail without this, so the message uses urgent (destructive)
 *    framing and cannot be dismissed.
 *  - Workspace integration (HubSpot / Salesforce): shown to admins only —
 *    affects the whole workspace. Uses amber/warning framing because the core
 *    product keeps working, just the integration stops syncing, and can be
 *    dismissed for the session.
 *
 * When more than one integration is disconnected, a single consolidated banner
 * is shown instead of stacking. Data comes from useDisconnectedAccounts, which
 * is preview-driven (localStorage) in Lovable and backend-driven in production.
 */

// Re-exported so PersonDetailSheet's debug panel keeps its import path.
export {
  setDisconnectedAccountPreview,
  type DisconnectedAccountVariant,
} from "@/lib/hooks/use-disconnected-accounts";

interface VariantConfig {
  logo: string;
  /** Vendor name — a brand, never translated. */
  providerLabel: string;
  titleKey: string;
  descriptionKey: string;
  reconnectTo: string;
}

const VARIANTS: Record<DisconnectedAccountVariant, VariantConfig> = {
  google: {
    logo: "/logos/google.svg",
    providerLabel: "Google",
    titleKey: "banners.disconnectedGoogleTitle",
    descriptionKey: "banners.disconnectedGoogleBody",
    reconnectTo: "/settings?section=accounts",
  },
  microsoft: {
    logo: "/logos/microsoft.svg",
    providerLabel: "Microsoft",
    titleKey: "banners.disconnectedMicrosoftTitle",
    descriptionKey: "banners.disconnectedMicrosoftBody",
    reconnectTo: "/settings?section=accounts",
  },
  "crm-hubspot": {
    logo: "/logos/hubspot-color.svg",
    providerLabel: "HubSpot",
    titleKey: "banners.disconnectedHubspotTitle",
    descriptionKey: "banners.disconnectedHubspotBody",
    reconnectTo: "/settings?section=integrations",
  },
  "crm-salesforce": {
    logo: "/logos/salesforce-color.svg",
    providerLabel: "Salesforce",
    titleKey: "banners.disconnectedSalesforceTitle",
    descriptionKey: "banners.disconnectedSalesforceBody",
    reconnectTo: "/settings?section=integrations",
  },
  slack: {
    logo: "/logos/slack.svg",
    providerLabel: "Slack",
    titleKey: "banners.disconnectedSlackTitle",
    descriptionKey: "banners.disconnectedSlackBody",
    reconnectTo: "/settings?section=integrations",
  },
};

// Personal account loss = destructive (red). Workspace integration loss =
// warning (amber) — product still works, just one connector is offline.
const DESTRUCTIVE_SURFACE =
  "bg-destructive/10 border-destructive/30 dark:bg-destructive/20 dark:border-destructive/40";
const WARNING_SURFACE =
  "bg-amber-500/10 border-amber-500/30 dark:bg-amber-500/15 dark:border-amber-500/40";
const DESTRUCTIVE_ICON = "text-destructive";
const WARNING_ICON = "text-amber-600 dark:text-amber-400";
const DESTRUCTIVE_CTA = "bg-destructive text-destructive-foreground hover:bg-destructive/90";
const WARNING_CTA =
  "bg-amber-600 text-white hover:bg-amber-700 dark:bg-amber-500 dark:hover:bg-amber-400 dark:text-amber-950";

export default function DisconnectedAccountBanner() {
  const { t } = useTranslation();
  const { variants, dismiss } = useDisconnectedAccounts();
  const navigate = useNavigate();

  /** Join provider labels into a readable list: "A, B and C". */
  const joinLabels = (labels: string[]): string => {
    if (labels.length <= 1) return labels[0] ?? "";
    return t("banners.listAnd", {
      items: labels.slice(0, -1).join(", "),
      last: labels[labels.length - 1],
    });
  };

  if (variants.length === 0) return null;

  // Any personal account loss makes the whole banner urgent (destructive).
  const hasPersonal = variants.some(v => VARIANT_SCOPE[v] === "personal");
  const surface = hasPersonal ? DESTRUCTIVE_SURFACE : WARNING_SURFACE;
  const iconColor = hasPersonal ? DESTRUCTIVE_ICON : WARNING_ICON;
  const ctaClass = hasPersonal ? DESTRUCTIVE_CTA : WARNING_CTA;
  // Dismiss is only offered when every banner is a (dismissible) workspace one.
  const dismissible = !hasPersonal;

  // --- Single disconnection: the original per-variant banner. ---
  if (variants.length === 1) {
    const variant = variants[0];
    const cfg = VARIANTS[variant];
    return (
      <div className={`w-full border-b px-4 py-2 ${surface}`}>
        <div className="flex items-center gap-3">
          <div className="relative shrink-0">
            <img src={cfg.logo} alt="" className="h-6 w-6 rounded-sm bg-background p-0.5" />
            <AlertTriangle
              className={`absolute -bottom-1 -right-1 h-3 w-3 ${iconColor} bg-background rounded-full`}
              strokeWidth={3}
            />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-foreground leading-tight">{t(cfg.titleKey)}</p>
            <p className="text-[11px] text-muted-foreground leading-snug truncate">
              {t(cfg.descriptionKey)}
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate(cfg.reconnectTo)}
            className={`shrink-0 inline-flex items-center h-7 px-3 rounded-md text-xs font-medium transition ${ctaClass}`}
          >
            {t("banners.disconnectedReconnect", { provider: cfg.providerLabel })}
          </button>
          {dismissible && (
            <button
              type="button"
              onClick={() => dismiss(variant)}
              className="shrink-0 rounded-md p-1 text-foreground/60 hover:text-foreground hover:bg-foreground/5"
              aria-label={t("banners.dismiss")}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
    );
  }

  // --- Multiple disconnections: one consolidated banner. ---
  const labels = variants.map(v => VARIANTS[v].providerLabel);
  // Send the user to accounts when a personal account is involved, else integrations.
  const reviewTo = hasPersonal ? "/settings?section=accounts" : "/settings?section=integrations";

  return (
    <div className={`w-full border-b px-4 py-2 ${surface}`}>
      <div className="flex items-center gap-3">
        <div className="relative shrink-0 flex items-center">
          {variants.slice(0, 3).map((v, i) => (
            <img
              key={v}
              src={VARIANTS[v].logo}
              alt=""
              className={`h-6 w-6 rounded-sm bg-background p-0.5 ring-1 ring-background ${i > 0 ? "-ml-2" : ""}`}
            />
          ))}
          <AlertTriangle
            className={`absolute -bottom-1 -right-1 h-3 w-3 ${iconColor} bg-background rounded-full`}
            strokeWidth={3}
          />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-foreground leading-tight">
            {t("banners.disconnectedCount", { count: variants.length })}
          </p>
          <p className="text-[11px] text-muted-foreground leading-snug truncate">
            {t("banners.disconnectedMultiBody", { providers: joinLabels(labels) })}
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate(reviewTo)}
          className={`shrink-0 inline-flex items-center h-7 px-3 rounded-md text-xs font-medium transition ${ctaClass}`}
        >
          {t("banners.disconnectedReviewInSettings")}
        </button>
        {dismissible && (
          <button
            type="button"
            onClick={() => variants.forEach(dismiss)}
            className="shrink-0 rounded-md p-1 text-foreground/60 hover:text-foreground hover:bg-foreground/5"
            aria-label={t("banners.dismiss")}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
